import { StyleSheet, Font } from '@react-pdf/renderer';

// =====================================
// Fuentes PDF (independientes de la web)
// =====================================
// La web sigue usando Rubik (definida en styles.css). Aquí registramos un
// par tipográfico solo para el PDF generado:
//   - Libre Baskerville (serif clasico) → títulos y headers
//   - Encode Sans (sans-serif moderno) → texto general (body, tablas, totales)
//
// Cargado vía Fontsource CDN (URLs estables de TTFs). El registro corre
// una sola vez a nivel de módulo.
Font.register({
    family: 'LibreBaskerville',
    fonts: [
        { src: 'https://cdn.jsdelivr.net/fontsource/fonts/libre-baskerville@latest/latin-400-normal.ttf', fontWeight: 400 },
        { src: 'https://cdn.jsdelivr.net/fontsource/fonts/libre-baskerville@latest/latin-700-normal.ttf', fontWeight: 700 },
        { src: 'https://cdn.jsdelivr.net/fontsource/fonts/libre-baskerville@latest/latin-400-italic.ttf', fontWeight: 400, fontStyle: 'italic' },
        { src: 'https://cdn.jsdelivr.net/fontsource/fonts/libre-baskerville@latest/latin-700-italic.ttf', fontWeight: 700, fontStyle: 'italic' }
    ]
});

// Encode Sans no tiene variantes italic diseñadas (el autor original nunca
// las publico). Para no perder el estilo italic al renderizar <em>/<strong><em>,
// registramos las italic de Nunito Sans bajo el mismo family: es humanista
// similar a Encode Sans, hay un leve font-mismatch pero preserva la distincion
// visual del texto en italic.
Font.register({
    family: 'EncodeSans',
    fonts: [
        { src: 'https://cdn.jsdelivr.net/fontsource/fonts/encode-sans@latest/latin-300-normal.ttf', fontWeight: 300 },
        { src: 'https://cdn.jsdelivr.net/fontsource/fonts/encode-sans@latest/latin-400-normal.ttf', fontWeight: 400 },
        { src: 'https://cdn.jsdelivr.net/fontsource/fonts/encode-sans@latest/latin-500-normal.ttf', fontWeight: 500 },
        { src: 'https://cdn.jsdelivr.net/fontsource/fonts/encode-sans@latest/latin-600-normal.ttf', fontWeight: 600 },
        { src: 'https://cdn.jsdelivr.net/fontsource/fonts/encode-sans@latest/latin-700-normal.ttf', fontWeight: 700 },
        { src: 'https://cdn.jsdelivr.net/fontsource/fonts/nunito-sans@latest/latin-400-italic.ttf', fontWeight: 400, fontStyle: 'italic' },
        { src: 'https://cdn.jsdelivr.net/fontsource/fonts/nunito-sans@latest/latin-700-italic.ttf', fontWeight: 700, fontStyle: 'italic' }
    ]
});

// Familias semanticas — usar estos en los styles para que el cambio de
// fuentes en el futuro sea un solo cambio aqui.
export const FONT_TITLE = 'LibreBaskerville';
export const FONT_BODY = 'EncodeSans';

// Desactivar hyphenation automatica (rompe palabras a la mitad)
Font.registerHyphenationCallback(word => [word]);

// =====================================
// Paleta y constantes
// =====================================
export const BRAND_COLOR = '#457373';
export const TEXT_COLOR = '#0f172a';
export const MUTED_COLOR = '#64748b';
export const BORDER_COLOR = '#cbd5e1';
export const HEADER_TEXT = '#ffffff';
export const TABLE_HEADER_BG = BRAND_COLOR;

// Margen del Page (todas las paginas)
export const PAGE_MARGIN = 40;

// Tabla: anchos
export const TABLE_COL_SERVICIO = 420;
export const TABLE_COL_IMPORTE = 90;
export const TABLE_TOTAL_WIDTH = TABLE_COL_SERVICIO + TABLE_COL_IMPORTE;

// =====================================
// StyleSheet
// =====================================
export const styles = StyleSheet.create({
    // Default: cuerpo en EncodeSans (todo el documento hereda esta familia
    // a menos que un estilo especifique fontFamily: FONT_TITLE).
    page: {
        fontFamily: FONT_BODY,
        fontSize: 10,
        color: TEXT_COLOR,
        padding: PAGE_MARGIN,
        lineHeight: 1.4
    },

    // ---- Header ----
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        marginBottom: 4
    },
    headerLogo: {
        width: 50,
        height: 50,
        objectFit: 'contain'
    },
    headerTitle: {
        fontFamily: FONT_TITLE,
        fontSize: 14,
        fontWeight: 700,
        color: BRAND_COLOR,
        flex: 1
    },

    // ---- Bloque empresa (izq) + datos cliente (der) ----
    infoRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 10,
        marginBottom: 20
    },
    infoCol: {
        flexDirection: 'column'
    },
    infoColRight: {
        alignItems: 'flex-end'
    },
    infoLine: {
        fontSize: 10,
        color: TEXT_COLOR,
        marginBottom: 2
    },

    // ---- Categoria header ----
    categoriaTitle: {
        fontFamily: FONT_TITLE,
        fontSize: 12,
        fontWeight: 700,
        color: BRAND_COLOR,
        marginTop: 6,
        marginBottom: 2
    },

    // ---- Tabla servicios ----
    tableHead: {
        flexDirection: 'row',
        backgroundColor: TABLE_HEADER_BG,
        paddingVertical: 6,
        paddingHorizontal: 8
    },
    tableHeadCell: {
        color: HEADER_TEXT,
        fontWeight: 700,
        fontSize: 10
    },
    tableHeadCellRight: {
        textAlign: 'right'
    },
    tableRow: {
        flexDirection: 'row',
        borderLeftWidth: 1,
        borderRightWidth: 1,
        borderBottomWidth: 1,
        borderColor: BORDER_COLOR
    },
    // Row solo con titulo + importe (parte superior del item — atomica).
    tableRowHeader: {
        flexDirection: 'row',
        borderLeftWidth: 1,
        borderRightWidth: 1,
        borderColor: BORDER_COLOR
    },
    // Row solo con el detalle largo (parte inferior — puede fluir entre paginas).
    tableRowBody: {
        flexDirection: 'row',
        borderLeftWidth: 1,
        borderRightWidth: 1,
        borderBottomWidth: 1,
        borderColor: BORDER_COLOR
    },
    tableCellServicio: {
        width: TABLE_COL_SERVICIO,
        padding: 8,
        borderRightWidth: 1,
        borderColor: BORDER_COLOR
    },
    // Cell titulo sin padding-bottom para pegarlo al detalle.
    tableCellServicioHeader: {
        width: TABLE_COL_SERVICIO,
        paddingHorizontal: 8,
        paddingTop: 8,
        paddingBottom: 0,
        borderRightWidth: 1,
        borderColor: BORDER_COLOR
    },
    // Cell detalle sin padding-top para pegarlo al titulo.
    tableCellServicioBody: {
        width: TABLE_COL_SERVICIO,
        paddingHorizontal: 8,
        paddingTop: 2,
        paddingBottom: 8,
        borderRightWidth: 1,
        borderColor: BORDER_COLOR
    },
    // Fila de cierre de la tabla de extras
    tableRowSubtotal: {
        flexDirection: 'row',
        borderLeftWidth: 1,
        borderRightWidth: 1,
        borderBottomWidth: 1,
        borderColor: BORDER_COLOR
    },
    tableCellSubtotalLabel: {
        width: TABLE_COL_SERVICIO,
        padding: 8,
        borderRightWidth: 1,
        borderColor: BORDER_COLOR,
        textAlign: 'right',
        fontSize: 11,
        fontWeight: 700,
        color: TEXT_COLOR
    },
    tableCellSubtotalValue: {
        width: TABLE_COL_IMPORTE,
        padding: 8,
        textAlign: 'right',
        fontSize: 12,
        fontWeight: 700,
        color: BRAND_COLOR
    },
    tableCellImporteEmpty: {
        width: TABLE_COL_IMPORTE
    },
    tableCellImporte: {
        width: TABLE_COL_IMPORTE,
        padding: 8,
        textAlign: 'right',
        fontSize: 12,
        fontWeight: 700,
        color: BRAND_COLOR
    },
    servicioNombre: {
        fontSize: 13,
        fontWeight: 700,
        marginBottom: 6,
        color: BRAND_COLOR,
        letterSpacing: 0.2,
        textTransform: 'uppercase'
    },
    // Sufijo "/mensual" DEBAJO del precio del servicio (compacto, gris).
    servicioPeriodoSuffix: {
        fontSize: 8,
        fontWeight: 400,
        color: MUTED_COLOR,
        textAlign: 'right',
        marginTop: 1
    },

    // ---- Costos fijos (sub-fila indentada dentro del servicio) ----
    // Mismo borde lateral que tableRow para que se vea continuo con el servicio
    // padre, pero con padding-left mayor y tipografia mas tenue para denotar jerarquia.
    costoFijoRow: {
        flexDirection: 'row',
        borderLeftWidth: 1,
        borderRightWidth: 1,
        borderBottomWidth: 1,
        borderColor: BORDER_COLOR,
        backgroundColor: '#fafafa'
    },
    costoFijoCellNombre: {
        width: TABLE_COL_SERVICIO,
        paddingTop: 6,
        paddingBottom: 6,
        paddingRight: 8,
        paddingLeft: 20, // indentacion respecto al servicio padre
        borderRightWidth: 1,
        borderColor: BORDER_COLOR
    },
    costoFijoNombre: {
        fontSize: 10,
        fontWeight: 600,
        color: '#333'
    },
    costoFijoDescripcion: {
        fontSize: 9.5,
        color: '#666',
        marginTop: 2,
        lineHeight: 1.35
    },
    costoFijoImporte: {
        fontSize: 10,
        fontWeight: 400,
        color: '#333'
    },

    // ---- Oferta comercial (descuento visible al cliente) ----
    // Precio original tachado antes del precio final.
    ofertaPrecioTachado: {
        fontSize: 9,
        fontWeight: 400,
        color: MUTED_COLOR,
        textDecoration: 'line-through',
        textAlign: 'right'
    },
    ofertaPrecioTachadoCf: {
        fontSize: 9,
        fontWeight: 400,
        color: MUTED_COLOR,
        textDecoration: 'line-through',
        textAlign: 'right'
    },
    // Precio final tras la oferta, destacado en el verde --success del proyecto.
    ofertaPrecioFinal: {
        color: '#16a34a',
        fontWeight: 700
    },
    // Etiqueta del descuento + motivo debajo del importe.
    ofertaMotivo: {
        fontSize: 8.5,
        fontWeight: 500,
        color: '#16a34a',
        textAlign: 'right',
        marginTop: 2,
        fontStyle: 'italic'
    },

    // ---- Fila con recurrentes + totales ----
    totalesRow: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'flex-start',
        marginTop: 16
    },
    // totalesRowRight = bloque de totales, alineado a la derecha ocupando 70%.
    // totalesRowLeft se mantiene disponible por si se reactiva PagosRecurrentes.
    totalesRowLeft: {
        width: '26%',
        minWidth: 0,
        marginRight: '4%'
    },
    totalesRowRight: {
        width: '70%',
        flexShrink: 0
    },

    // ---- Totales ----
    totalesWrap: {
        width: '100%'
    },
    totalRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 6
    },
    totalLabel: {
        fontSize: 12
    },
    totalValue: {
        fontSize: 12,
        textAlign: 'right'
    },
    totalRowBold: {
        marginTop: 8,
        paddingTop: 8,
        borderTopWidth: 1,
        borderColor: BORDER_COLOR
    },
    totalLabelBold: {
        fontSize: 14,
        fontWeight: 700
    },
    totalValueBold: {
        fontSize: 14,
        fontWeight: 700,
        textAlign: 'right',
        color: BRAND_COLOR
    },

    // ---- Totales con desglose por periodo (reemplaza al `Totales` simple) ----
    tdWrap: {
        width: '100%'
    },
    tdGrupo: {
        marginBottom: 8
    },
    tdGrupoTitle: {
        fontFamily: FONT_TITLE,
        fontSize: 10,
        fontWeight: 700,
        color: MUTED_COLOR,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        marginBottom: 3,
        borderBottomWidth: 1,
        borderBottomColor: BORDER_COLOR,
        paddingBottom: 2
    },
    tdRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        marginBottom: 3
    },
    tdLabel: {
        fontSize: 10,
        color: TEXT_COLOR,
        flex: 1,
        paddingRight: 8
    },
    tdValue: {
        fontSize: 10,
        color: TEXT_COLOR,
        textAlign: 'right'
    },
    tdPeriodo: {
        fontSize: 8,
        color: MUTED_COLOR,
        fontWeight: 400
    },
    tdBloqueSubtotales: {
        marginTop: 6,
        paddingTop: 6,
        borderTopWidth: 1,
        borderTopColor: BORDER_COLOR
    },
    tdLabelSubtotal: {
        fontSize: 11,
        fontWeight: 700,
        color: TEXT_COLOR
    },
    tdValueSubtotal: {
        fontSize: 11,
        fontWeight: 700,
        color: TEXT_COLOR,
        textAlign: 'right'
    },
    // Descuentos aplicados — mismo color que los otros precios del bloque
    tdLabelDescuento: {
        fontSize: 11,
        fontWeight: 600,
        color: TEXT_COLOR
    },
    tdValueDescuento: {
        fontSize: 11,
        fontWeight: 700,
        color: TEXT_COLOR,
        textAlign: 'right'
    },
    tdBloqueTotal: {
        marginTop: 6,
        paddingTop: 6,
        borderTopWidth: 1,
        borderTopColor: BORDER_COLOR
    },
    tdRowTotal: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        marginTop: 4
    },
    tdLabelTotal: {
        fontSize: 14,
        fontWeight: 700,
        color: TEXT_COLOR
    },
    tdValueTotal: {
        fontSize: 14,
        fontWeight: 700,
        color: BRAND_COLOR,
        textAlign: 'right'
    },
    tdNota: {
        marginTop: 8,
        fontSize: 8.5,
        color: MUTED_COLOR,
        fontStyle: 'italic',
        lineHeight: 1.4
    },
    // Card del TOTAL final — fondo con el color de marca (BRAND_COLOR),
    // monto grande a la izquierda y nota explicativa a la derecha.
    // Fuente sans-serif (FONT_BODY) para dar look moderno/dashboard, distinto
    // del serif que usa el resto de titulares del PDF. Todo el texto en blanco.
    tdTotalCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: BRAND_COLOR,
        borderRadius: 6,
        paddingVertical: 20,
        paddingHorizontal: 20,
        marginTop: 6
    },
    tdTotalCardLeft: {
        flex: 1.2,
        paddingRight: 16
    },
    tdTotalCardLabel: {
        fontFamily: FONT_BODY,
        fontSize: 9,
        fontWeight: 700,
        color: '#ffffff',
        letterSpacing: 1.2,
        textTransform: 'uppercase',
        marginBottom: 2,
        lineHeight: 1
    },
    tdTotalCardMonto: {
        fontFamily: FONT_BODY,
        fontSize: 26,
        fontWeight: 800,
        color: '#ffffff',
        letterSpacing: -0.5,
        lineHeight: 1.1
    },
    tdTotalCardRight: {
        flex: 1,
        borderLeftWidth: 1,
        borderLeftColor: '#ffffff',
        paddingLeft: 16
    },
    tdTotalCardNota: {
        fontFamily: FONT_BODY,
        fontSize: 8,
        color: '#ffffff',
        lineHeight: 1.4,
        textAlign: 'right'
    },
    // ---- Plan de pago (bloque al final de TotalesConDesglose) ----
    // Card discreto en gris claro con las cuotas del plan seleccionado.
    tdPlanPagoWrap: {
        marginTop: 12,
        padding: 12,
        borderRadius: 6,
        backgroundColor: '#f5f7f8',
        borderWidth: 1,
        borderColor: BORDER_COLOR
    },
    tdPlanPagoTitulo: {
        fontFamily: FONT_BODY,
        fontSize: 11,
        fontWeight: 700,
        color: BRAND_COLOR,
        marginBottom: 3,
        textTransform: 'uppercase',
        letterSpacing: 0.4
    },
    tdPlanPagoDesc: {
        fontFamily: FONT_BODY,
        fontSize: 9,
        color: MUTED_COLOR,
        marginBottom: 8,
        lineHeight: 1.4
    },
    tdPlanPagoLista: {
        flexDirection: 'column',
        gap: 4
    },
    tdPlanPagoRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 3,
        borderTopWidth: 1,
        borderTopColor: BORDER_COLOR
    },
    tdPlanPagoNum: {
        fontFamily: FONT_BODY,
        fontSize: 10,
        fontWeight: 700,
        color: BRAND_COLOR,
        width: 32
    },
    tdPlanPagoNombre: {
        fontFamily: FONT_BODY,
        fontSize: 10,
        color: TEXT_COLOR,
        flex: 1
    },
    tdPlanPagoPct: {
        fontFamily: FONT_BODY,
        fontSize: 9,
        color: MUTED_COLOR,
        width: 60,
        textAlign: 'right',
        paddingRight: 12
    },
    tdPlanPagoMonto: {
        fontFamily: FONT_BODY,
        fontSize: 11,
        fontWeight: 700,
        color: TEXT_COLOR,
        width: 90,
        textAlign: 'right'
    },

    // Wrapper legacy — mantenido por compat, no se usa ya.
    tdOpcionalWrap: {
        width: '60%',
        alignSelf: 'flex-end',
        marginTop: 10,
        paddingTop: 6,
        borderTopWidth: 1,
        borderTopColor: BORDER_COLOR
    },

    // ---- Desglose Opcionales (toc*): variantes reducidas de td* ----
    // Mismo formato que TotalesConDesglose pero fonts ~2px menores y sin colores
    // de marca en subtotales/total, para no competir con el bloque principal.
    // Alineado a la derecha ocupando 70% del ancho.
    tocWrap: {
        width: '70%',
        alignSelf: 'flex-end',
        marginTop: 10,
        paddingTop: 6,
        borderTopWidth: 1,
        borderTopColor: BORDER_COLOR
    },
    tocGrupo: {
        marginBottom: 6
    },
    tocGrupoTitle: {
        fontFamily: FONT_TITLE,
        fontSize: 8.5,
        fontWeight: 700,
        color: MUTED_COLOR,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
        marginBottom: 2,
        borderBottomWidth: 1,
        borderBottomColor: BORDER_COLOR,
        paddingBottom: 1
    },
    tocRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        marginBottom: 2
    },
    tocLabel: {
        fontSize: 8.5,
        color: MUTED_COLOR,
        flex: 1,
        paddingRight: 6
    },
    tocValue: {
        fontSize: 8.5,
        color: MUTED_COLOR,
        textAlign: 'right'
    },
    tocPeriodo: {
        fontSize: 7,
        color: MUTED_COLOR,
        fontWeight: 400,
        opacity: 0.85
    },
    tocBloqueSubtotales: {
        marginTop: 4,
        paddingTop: 4,
        borderTopWidth: 1,
        borderTopColor: BORDER_COLOR
    },
    tocLabelSubtotal: {
        fontSize: 9,
        fontWeight: 700,
        color: MUTED_COLOR
    },
    tocValueSubtotal: {
        fontSize: 9,
        fontWeight: 700,
        color: MUTED_COLOR,
        textAlign: 'right'
    },
    tocBloqueTotal: {
        marginTop: 4,
        paddingTop: 4,
        borderTopWidth: 1,
        borderTopColor: BORDER_COLOR
    },
    tocRowTotal: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        marginTop: 3
    },
    tocLabelTotal: {
        fontSize: 11,
        fontWeight: 700,
        color: '#334155'
    },
    tocValueTotal: {
        fontSize: 11,
        fontWeight: 700,
        color: '#334155',
        textAlign: 'right'
    },

    // ---- Bloque Opcionales ----
    // Solo el TITULO del bloque queda muted; la tabla usa colores de marca
    // pero con tamanios ~2px menores para que se lea como anexo secundario.
    opcionalesWrap: {
        marginTop: 18,
        marginBottom: 20
    },
    opcionalesTitle: {
        fontFamily: FONT_TITLE,
        fontSize: 11,
        fontWeight: 700,
        color: MUTED_COLOR,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
        marginBottom: 2
    },
    opcionalesDesc: {
        fontSize: 8.5,
        color: MUTED_COLOR,
        marginBottom: 8,
        fontStyle: 'italic'
    },
    opcCategoriaTitle: {
        fontFamily: FONT_TITLE,
        fontSize: 10,
        fontWeight: 700,
        color: BRAND_COLOR,
        marginTop: 6,
        marginBottom: 2
    },
    opcTableHead: {
        flexDirection: 'row',
        backgroundColor: TABLE_HEADER_BG,
        paddingVertical: 5,
        paddingHorizontal: 7
    },
    opcTableHeadCell: {
        color: HEADER_TEXT,
        fontWeight: 700,
        // 1pt menos que tableHeadCell (que es 10pt) — mantiene jerarquia sutil.
        fontSize: 9
    },
    opcTableRow: {
        flexDirection: 'row',
        borderLeftWidth: 1,
        borderRightWidth: 1,
        borderBottomWidth: 1,
        borderColor: BORDER_COLOR
    },
    // Variantes header/body para poder separar titulo (atomico) del detalle (fluye).
    opcTableRowHeader: {
        flexDirection: 'row',
        borderLeftWidth: 1,
        borderRightWidth: 1,
        borderColor: BORDER_COLOR
    },
    opcTableRowBody: {
        flexDirection: 'row',
        borderLeftWidth: 1,
        borderRightWidth: 1,
        borderBottomWidth: 1,
        borderColor: BORDER_COLOR
    },
    opcCellServicio: {
        width: TABLE_COL_SERVICIO,
        padding: 7,
        borderRightWidth: 1,
        borderColor: BORDER_COLOR
    },
    opcCellServicioHeader: {
        width: TABLE_COL_SERVICIO,
        paddingHorizontal: 7,
        paddingTop: 7,
        paddingBottom: 0,
        borderRightWidth: 1,
        borderColor: BORDER_COLOR
    },
    opcCellServicioBody: {
        width: TABLE_COL_SERVICIO,
        paddingHorizontal: 7,
        paddingTop: 2,
        paddingBottom: 7,
        borderRightWidth: 1,
        borderColor: BORDER_COLOR
    },
    opcCellImporteEmpty: {
        width: TABLE_COL_IMPORTE
    },
    opcCellImporte: {
        width: TABLE_COL_IMPORTE,
        padding: 7,
        textAlign: 'right',
        fontSize: 10,
        fontWeight: 700,
        color: BRAND_COLOR
    },
    opcServicioNombre: {
        fontSize: 11,
        fontWeight: 700,
        marginBottom: 5,
        color: BRAND_COLOR,
        letterSpacing: 0.2,
        textTransform: 'uppercase'
    },
    opcCostoFijoRow: {
        flexDirection: 'row',
        borderLeftWidth: 1,
        borderRightWidth: 1,
        borderBottomWidth: 1,
        borderColor: BORDER_COLOR,
        backgroundColor: '#fafafa'
    },
    opcCostoFijoCellNombre: {
        width: TABLE_COL_SERVICIO,
        paddingTop: 5,
        paddingBottom: 5,
        paddingRight: 7,
        paddingLeft: 18,
        borderRightWidth: 1,
        borderColor: BORDER_COLOR
    },
    opcCostoFijoNombre: {
        fontSize: 9,
        fontWeight: 600,
        color: '#333'
    },
    opcCostoFijoDescripcion: {
        fontSize: 8.5,
        color: '#666',
        marginTop: 2,
        lineHeight: 1.35
    },
    opcCostoFijoImporte: {
        fontSize: 9,
        fontWeight: 400,
        color: '#333'
    },
    // Total row ocupa el ancho de la tabla (label izquierda, importe derecha)
    opcTotalRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        width: TABLE_COL_SERVICIO + TABLE_COL_IMPORTE,
        marginTop: 8,
        paddingTop: 6,
        paddingHorizontal: 4,
        borderTopWidth: 1,
        borderColor: BORDER_COLOR
    },
    opcTotalLabel: {
        fontSize: 11,
        fontWeight: 700,
        color: MUTED_COLOR
    },
    opcTotalValue: {
        fontSize: 11,
        fontWeight: 700,
        color: BRAND_COLOR,
        textAlign: 'right'
    },

    // ---- Pagos Recurrentes (bloque a la izquierda del total) ----
    pagosRecWrap: {
        padding: 12,
        backgroundColor: '#f8fafc',
        border: `1px solid ${BORDER_COLOR}`,
        borderRadius: 6
    },
    pagosRecTitle: {
        fontFamily: FONT_TITLE,
        fontSize: 11,
        fontWeight: 700,
        color: BRAND_COLOR,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
        marginBottom: 4
    },
    pagosRecDesc: {
        fontSize: 8.5,
        color: MUTED_COLOR,
        lineHeight: 1.4,
        marginBottom: 8
    },
    pagosRecTable: {
        marginTop: 4
    },
    pagosRecRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        paddingVertical: 3,
        borderBottomWidth: 0.5,
        borderBottomColor: BORDER_COLOR
    },
    pagosRecLabel: {
        fontSize: 9.5,
        color: TEXT_COLOR,
        flex: 1,
        paddingRight: 8
    },
    pagosRecValue: {
        fontSize: 9.5,
        color: TEXT_COLOR,
        textAlign: 'right'
    },
    pagosRecPeriodo: {
        fontSize: 8,
        color: MUTED_COLOR
    },
    pagosRecRowTotal: {
        marginTop: 4,
        paddingTop: 6,
        borderTopWidth: 1,
        borderTopColor: BORDER_COLOR,
        borderBottomWidth: 0
    },
    pagosRecLabelBold: {
        fontSize: 10,
        fontWeight: 700,
        color: TEXT_COLOR,
        flex: 1
    },
    pagosRecValueBold: {
        fontSize: 10,
        fontWeight: 700,
        color: BRAND_COLOR,
        textAlign: 'right'
    },

    // ---- Comentarios / Metodos de pago ----
    sectionTitle: {
        fontFamily: FONT_TITLE,
        fontSize: 11,
        fontWeight: 700,
        marginTop: 18,
        marginBottom: 6
    },

    // ---- Espaciado entre categorias ----
    categoriaGap: {
        height: 16
    },

    // ---- Footer fijo (paginacion) ----
    // Contenedor absolutamente posicionado al pie de cada pagina. `fixed` en
    // el <View> hace que se repita automaticamente en todas las paginas.
    // bottom: 18 cae dentro del padding inferior del Page (PAGE_MARGIN=40),
    // dejando ~22pt de aire entre el footer y el borde del PDF.
    footerContainer: {
        position: 'absolute',
        bottom: 18,
        left: PAGE_MARGIN,
        right: PAGE_MARGIN
    },
    footerPageNumber: {
        fontSize: 8.5,
        color: MUTED_COLOR,
        textAlign: 'right'
    }
});

// Estilos del HtmlRenderer (texto rich)
export const richStyles = StyleSheet.create({
    paragraph: {
        marginBottom: 6,
        fontSize: 10,
        lineHeight: 1.4
    },
    bold: {
        fontWeight: 700
    },
    italic: {
        fontStyle: 'italic'
    },
    underline: {
        textDecoration: 'underline'
    },
    link: {
        color: BRAND_COLOR,
        textDecoration: 'underline'
    },
    heading1: {
        fontFamily: FONT_TITLE,
        fontSize: 14,
        fontWeight: 700,
        marginTop: 8,
        marginBottom: 4
    },
    heading2: {
        fontFamily: FONT_TITLE,
        fontSize: 12,
        fontWeight: 700,
        marginTop: 6,
        marginBottom: 4
    },
    heading3: {
        fontFamily: FONT_TITLE,
        fontSize: 11,
        fontWeight: 700,
        marginTop: 4,
        marginBottom: 4
    },
    listItem: {
        flexDirection: 'row',
        marginBottom: 2
    },
    listBullet: {
        width: 12,
        fontSize: 10
    },
    listContent: {
        flex: 1,
        fontSize: 10
    },
    alignLeft: { textAlign: 'left' },
    alignCenter: { textAlign: 'center' },
    alignRight: { textAlign: 'right' },
    alignJustify: { textAlign: 'justify' }
});
